import { Head, Link, router, usePage } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { ArrowLeft, Trash2 } from 'lucide-react'
import { PageProps } from '@/types'

interface OrphanUser {
  id: number
  email: string
  role: string
  created_at: string
}

interface Props extends PageProps {
  orphans: OrphanUser[]
}

export default function OrphanUsersIndex({ orphans }: Props) {
  const flash = (usePage().props.flash as { notice?: string } | undefined) || {}

  function handleDelete(u: OrphanUser) {
    const ok = confirm(
      `Permanently delete orphan user ${u.email}?\n\nThis User row has no matching Agent and was left behind by a failed create. This cannot be undone.`
    )
    if (!ok) return
    router.delete(`/admin/orphan_users/${u.id}`)
  }

  return (
    <AppLayout>
      <Head title="Orphan Users" />
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/agents" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Orphan Users</h1>
            <p className="text-sm text-chestnut-charcoal/60 mt-1">
              User accounts with role=agent but no matching Agent record. Left behind by failed create attempts. Safe to delete — the email will become available again.
            </p>
          </div>
        </div>

        {flash.notice && (
          <div className="rounded border border-status-success/40 bg-status-success/10 px-4 py-3 text-sm text-status-success">
            {flash.notice}
          </div>
        )}

        <div className="card">
          {orphans.length === 0 ? (
            <p className="text-sm text-chestnut-charcoal/60">No orphan users in your organization. 🎉</p>
          ) : (
            <table className="min-w-full divide-y divide-chestnut-gray">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-chestnut-charcoal/60">
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Created</th>
                  <th className="py-2 pr-4" />
                </tr>
              </thead>
              <tbody className="divide-y divide-chestnut-gray">
                {orphans.map((u) => (
                  <tr key={u.id}>
                    <td className="py-3 pr-4 font-medium text-chestnut-charcoal">{u.email}</td>
                    <td className="py-3 pr-4 text-sm text-chestnut-charcoal/70">
                      {new Date(u.created_at).toLocaleString()}
                    </td>
                    <td className="py-3 pr-4 text-right">
                      <button
                        onClick={() => handleDelete(u)}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-status-error hover:underline"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
